import React, { memo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArtistGrid from '../components/ArtistGrid';
import PlayButton from '../components/PlayButton';
import Loading from '../components/Loading';
import ErrorDisplay from '../components/ErrorDisplay';
import { useArtists, useExhibitionArtists } from '../hooks/useArtists';
import { useArtworks } from '../hooks/useArtworks';
import { useExhibitions } from '../hooks/useExhibitions';
import { Artist } from '../types';
import { useExhibitionContext } from '../contexts/ExhibitionContext';
import { formatExhibitionName } from '../utils/formatExhibitionName';

const Exhibition: React.FC = memo(() => {
  const navigate = useNavigate();
  const { selectedExhibition, setSelectedExhibition } = useExhibitionContext();
  const { exhibitions } = useExhibitions({ limit: 10 });
  
  // Set default exhibition if none is selected
  useEffect(() => {
    if (!selectedExhibition && exhibitions.length > 0) {
      // Set the first exhibition as default (which should be "מחזור ז׳")
      setSelectedExhibition(exhibitions[0]);
    }
  }, [exhibitions, selectedExhibition, setSelectedExhibition]);
  
  // Fetch artists based on selected exhibition
  const { artists: allArtists, isLoading: allArtistsLoading, error: allArtistsError, refetch: refetchAllArtists } = useArtists({ limit: 100 });
  const { data: exhibitionArtistsData, isLoading: exhibitionArtistsLoading, error: exhibitionArtistsError, refetch: refetchExhibitionArtists } = useExhibitionArtists(selectedExhibition?.id || null);
  const { data: artworksData, isLoading: artworksLoading, error: artworksError, refetch: refetchArtworks } = useArtworks({ limit: 100 });
  
  // Use exhibition artists if available, otherwise use all artists
  const artistsToShow = selectedExhibition ? (exhibitionArtistsData?.data || []) : allArtists;
  const isLoading = selectedExhibition ? exhibitionArtistsLoading : allArtistsLoading;
  const artistsError = selectedExhibition ? exhibitionArtistsError : allArtistsError;
  const refetchArtists = selectedExhibition ? refetchExhibitionArtists : refetchAllArtists;

  const handleArtistClick = (slug: string) => {
    navigate(`/artist/${slug}`);
    window.scrollTo(0, 0);
  };

  // Show loading state
  if (isLoading || artworksLoading) {
    return <Loading message="טוען אמנים..." fullScreen />;
  }

  // Show error state
  if (artistsError || artworksError) {
    return (
      <ErrorDisplay 
        error={artistsError || artworksError || 'שגיאה בטעינת הנתונים'}
        onRetry={() => {
          refetchArtists();
          refetchArtworks();
        }}
        fullScreen
        title="שגיאה בטעינת התערוכה"
      />
    );
  }

  // Transform and filter artists with artworks
  const artistsWithArtworks: Artist[] = artistsToShow
    .map(artist => {
      // Find first artwork for this artist
      const firstArtwork = artworksData?.data?.find(artwork => artwork.artist_id === artist.id);

      return {
        ...artist,
        title: artist.statement ? artist.statement.substring(0, 50) + '...' : 'ללא תיאור',
        // Prioritize primary artwork image, fallback to first artwork image
        image: artist.primary_artwork?.image_url || firstArtwork?.image_url,
        hasArtwork: !!(artist.primary_artwork || firstArtwork)
      };
    })
    .filter(artist => artist.hasArtwork);

  // Get exhibition title and description - Format: "תערוכה שנתית" + exhibition name
  const exhibitionTitle = selectedExhibition?.name
    ? `תערוכה שנתית ${formatExhibitionName(selectedExhibition.name)}`
    : 'תערוכה שנתית מחזור ז׳ | 2025';

  return (
    <div className="view active">
      {/* Exhibition Title */}
      <section className="exhibition-title-section">
        <div className="content-container">
          <h1 className="exhibition-title" dir="rtl">{exhibitionTitle}</h1>
        </div>
      </section>

      {/* Hero Section */}
      <section className="exhibition-hero">
        <div className="exhibition-hero-image">
          {selectedExhibition?.video_url ? (
            <div className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe 
                src={`${selectedExhibition.video_url}?autoplay=1&mute=1&loop=1&playlist=${selectedExhibition.video_url.split('/').pop()}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title="Exhibition Video"
              />
            </div>
          ) : (
            <>
              <img 
                src={selectedExhibition?.image_url || "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80"} 
                alt={selectedExhibition?.name || "תערוכה שנתית מחזור ז 2025"} 
                id="exhibitionHeroImage"
              />
              <PlayButton />
            </>
          )}
        </div>
      </section>

      {/* Opening Text Section - Show only if opening_text exists */}
      {selectedExhibition?.opening_text && (
        <section className="guiding-text">
          <div className="content-container">
            {selectedExhibition.opening_text_title && (
              <h2 className="section-title" style={{ fontWeight: 'bold' }}>
                {selectedExhibition.opening_text_title}
              </h2>
            )}
            <div className="guiding-text-columns">
              {(() => {
                // Split text by double newlines to get paragraphs
                const paragraphs = selectedExhibition.opening_text
                  .split('\n\n')
                  .filter(p => p.trim());

                // Split after the 4th paragraph (index 0-3 in left, 4+ in right)
                // This makes the paragraph starting with "מאחורי הקלעים" the first in the right column
                const splitIndex = 4;

                const leftColumn = paragraphs.slice(0, splitIndex);
                const rightColumn = paragraphs.slice(splitIndex);

                return (
                  <>
                    <div className="guiding-text-column">
                      {leftColumn.map((paragraph, index) => (
                        <p key={`left-${index}`} className="guiding-text-paragraph">
                          {paragraph.trim()}
                        </p>
                      ))}
                    </div>
                    <div className="guiding-text-column">
                      {rightColumn.map((paragraph, index) => (
                        <p key={`right-${index}`} className="guiding-text-paragraph">
                          {paragraph.trim()}
                        </p>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Artists Grid Section */}
      <section className="artists-grid">
        <div className="content-container">
          {artistsWithArtworks.length > 0 ? (
            <ArtistGrid artistsData={artistsWithArtworks} onArtistClick={handleArtistClick} />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">אין אמנים זמינים כרגע</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
});

Exhibition.displayName = 'Exhibition';

export default Exhibition;